import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { XMLParser, XMLValidator } from 'fast-xml-parser';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { Integration, MIPIntegrationRegistration } from 'lib/types';
import {
    createIntegrationThunk,
    updateIntegrationThunk,
    resetCreateIntegrationStatus,
} from 'reducers/integrations/actions';
import { RootState } from 'reducers';
import { Status } from 'reducers/enum';

import Button from 'components/Button/Button';
import { TextArea, TextareaResizable } from 'components/TextArea/TextArea';
import LoaderOverlay from 'components/LoaderOverlay/LoaderOverlay';
import { useEffect } from 'react';
import { removeError } from 'reducers/integrations/actions';

export default function IntegrationEditor(props: IntegrationEditorProps) {
    const dispatch = useDispatch();
    const { integrationId } = useParams();
    const accountId = useSelector(
        (state: RootState) => state.credentials.accountId
    );
    const createIntegrationStatus = useSelector(
        (state: RootState) =>
            state.integrations.createUpdateOrDeleteIntegrationStatus
    );

    const error = useSelector((state: RootState) => state.integrations.error);

    const validationSchema = yup.object({
        xml: yup.string(),
    });

    const handleOnSubmit = (values: IntegrationFormikValues) => {
        const { xml } = values;
        const parser = new XMLParser();

        if (XMLValidator.validate(xml) !== true) {
            formik.setFieldError('xml', 'The XML is invalid.');
            return;
        }

        const integrationRegistration: MIPIntegrationRegistration =
            parser.parse(xml);

        const mipLicenseInformation =
            integrationRegistration?.['MIPLicenseInformation'];

        if (mipLicenseInformation == null) {
            formik.setFieldError('xml', 'The XML is invalid.');
            return;
        }

        const id = mipLicenseInformation['PluginId'];
        const name = mipLicenseInformation['Name'];

        if (!id) {
            formik.setFieldError('xml', 'The XML is invalid.');
            return;
        }
        if (!name) {
            formik.setFieldError('xml', 'The XML is invalid.');
            return;
        }

        // licenses.license may be parsed as an object or an array, depending on the number of licenses
        // Therefore, we may need to map the object to an array of objects
        let mipLicenseTypes =
            mipLicenseInformation['Licenses']?.['License'] ?? [];

        if (!mipLicenseTypes) {
            formik.setFieldError('xml', 'The XML is invalid.');
            return;
        }

        if (!Array.isArray(mipLicenseTypes)) {
            mipLicenseTypes = [mipLicenseTypes];
        }

        if (
            mipLicenseTypes.length === 0 ||
            mipLicenseTypes.some((licenseType) => !licenseType['Type'])
        ) {
            formik.setFieldError('xml', 'The XML is invalid.');
            return;
        }

        const licenseTypes = mipLicenseTypes.map((licenseType) => ({
            name: licenseType['Type'].toString(),
        }));

        const mipIntegration = {
            id: id.toString(),
            name: name.toString(),
            accountId,
            licenseTypes,
        };

        if (integrationId) {
            dispatch(
                updateIntegrationThunk({
                    integration: mipIntegration,
                })
            );
            return;
        }

        dispatch(createIntegrationThunk(mipIntegration));
    };

    const formik = useFormik({
        initialValues: {
            xml: '',
        },
        validationSchema: validationSchema,
        onSubmit: handleOnSubmit,
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (createIntegrationStatus === Status.Finished && error == '') {
            dispatch(resetCreateIntegrationStatus());
            navigate('/', { replace: true });
            return;
        }

        formik.setFieldError('xml', error);        
    }, [createIntegrationStatus, error, dispatch, navigate]);

    const handleCancelButtonPressed = () => {
        navigate('/integrations');
    };

    return (
        <>
            <LoaderOverlay
                isLoading={createIntegrationStatus === Status.Pending}
            />
            <form onSubmit={formik.handleSubmit}>
                <TextArea
                    label="Integration XML"
                    name="xml"
                    value={formik.values.xml}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                        formik.touched.xml &&
                        (formik.errors.xml as string | string[])
                    }
                    resizable={TextareaResizable.Vertical}
                />

                <div className="msds-btn-group mt-6">
                    <Button color="primary" type="submit">
                        Save
                    </Button>
                    <Button
                        color="secondary"
                        onClick={handleCancelButtonPressed}
                    >
                        Cancel
                    </Button>
                </div>
            </form>
        </>
    );
}

type IntegrationFormikValues = {
    xml: string;
};

type IntegrationEditorProps = {
    integration: Integration;
};
